import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useFormContext } from 'react-hook-form';
import { makeStyles } from 'tss-react/mui';
import _isEmpty from 'lodash/isEmpty';
import _cloneDeep from 'lodash/cloneDeep';
import { gridTags, ITaskGridBody, TaskEntity } from 'app/models/Tasks/Tasks';
import { selectTaskEntity, selectTaskGroupedList } from '../store/Tasks.selectors';
import { GridColumns } from '@mui/x-data-grid-pro';
import { XGridDefaultColumn } from 'app/components/Common/XGrid/cells/XGridDefaults';
import { XGrid } from 'app/components/Common/XGrid/XGrid';
import { usePromiseTracker } from 'react-promise-tracker';
import { taskDetailsActions } from '../store/Tasks.redux';
import { getDetailsPageSupportComponentHeight } from 'app/layout/pages/DetailsPage';
import { selectGridState } from 'app/components/Common/Grid/GridStateManagement/GridState.selectors';
import { gridStateActions } from 'app/components/Common/Grid/GridStateManagement/GridState.redux';
import { totalCountErrorThreshold } from 'store/redux-store/global-search/slice';
import { GridSortModel, GridCallbackDetails, GridRowParams } from '@mui/x-data-grid-pro';
import { useGridApiRef } from '@mui/x-data-grid-pro';
import { missingField } from 'app/constants';
import { globalActions } from 'app/ApplicationRoot/Global.redux';
import { checkRequiredFilters, defaultTaskWordDeskColumn, formatPlacementGridDetails } from './Transformers/helper';
import { isEqual } from 'lodash';
import { SearchType } from 'app/models/GlobalSearch/GlobalSearch';
import { SearchRecommendation } from './SearchRecommendation';

const useStyles = makeStyles()(theme => ({
  groupContainer: {
    '& .MuiDataGrid-root': {
      '& .MuiDataGrid-footer': {
        '& .MuiPagination-ul': {
          justifyContent: 'space-between',
        },
      },
    },
  },
  columnValues: {
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
    display: 'grid',
    flexDirection: 'column',
  },
}));

export const TaskEntityGroupedList = () => {
  const { t } = useTranslation();
  const { classes } = useStyles();
  const dispatch = useDispatch();
  const { setValue } = useFormContext();
  const taskEntity = useSelector(selectTaskEntity);
  const rows = useSelector(selectTaskGroupedList);
  const defaultSortModel: any[] = [
    {
      field: 'entityId',
      sort: 'desc',
    },
  ];
  const { filterAttributes } = useSelector(selectGridState);
  const { promiseInProgress: loading } = usePromiseTracker({ area: 'get-task-grouped-list', delay: 0 });
  const [dontUpdateData, setDontUpdateData] = useState<boolean>(false);
  const [columns, setColumns] = useState<GridColumns>([]);
  const [gridDetails, setGridDetails] = useState<any[]>([]);
  const [sortModel, setSortModel] = useState<any>(defaultSortModel);
  const [searchSuggestion, setSearchSuggestion] = useState<boolean>();
  const [sortedColumn, setSortedColumn] = useState<any>({
    column: 'entityId',
    direction: 'desc',
  });
  const [gridHeight, setGridHeight] = useState<number | undefined>(window.innerHeight / 1.9);
  const gridApi = useGridApiRef();

  useEffect(() => {
    const requiredFiltersErrors = checkRequiredFilters(filterAttributes?.selectedChipFilters);
    if (!dontUpdateData) {
      if (filterAttributes && !_isEmpty(filterAttributes?.selectedChipFilters) && !requiredFiltersErrors) {
        setSearchSuggestion(false);
        fetchTaskGroupedListData(50, 1, {
          column: 'entityId',
          direction: 'desc',
        });
      } else {
        dispatch(
          taskDetailsActions.setTaskGroupedList({
            paging: undefined,
            items: [],
          }),
        );
        setSearchSuggestion(true);
      }
    }
    setDontUpdateData(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [taskEntity, filterAttributes?.selectedChipFilters]);

  const fetchTaskGroupedListData = (offset, page, passedSortedColumn) => {
    const body: ITaskGridBody = {
      entity: taskEntity,
      pageNumber: page,
      pageSize: offset,
      candidateId: filterAttributes?.selectedChipFilters?.candidateIds,
      placementId: filterAttributes?.selectedChipFilters?.placementIds,
      facilityId: filterAttributes?.selectedChipFilters?.facilityIds,
      orderId: filterAttributes?.selectedChipFilters?.orderIds,
      taskNameId: filterAttributes?.selectedChipFilters?.taskName,
      taskCategory: filterAttributes?.selectedChipFilters?.taskCategories,
      dueDateStart: filterAttributes?.selectedChipFilters?.dueDateStart
        ? filterAttributes?.selectedChipFilters?.dueDateStart
        : null,
      dueDateEnd: filterAttributes?.selectedChipFilters?.dueDateEnd
        ? filterAttributes?.selectedChipFilters?.dueDateEnd
        : null,
      assignedTo: filterAttributes?.selectedChipFilters?.assignedToIds,
      createdBy: filterAttributes?.selectedChipFilters?.createdByIds,
      taskStatusId: filterAttributes?.selectedChipFilters?.taskStatusIds,
      sortedColumn: passedSortedColumn,
      translation: t,
    };
    dispatch(taskDetailsActions.getTaskGroupedListAction(body));
  };

  const returnEntityTitle = () => {
    switch (taskEntity) {
      case TaskEntity.CANDIDATES:
        return t('notification.taskManagement.groupedList.candidateTasks');
      case TaskEntity.PLACEMENTS:
        return t('notification.taskManagement.groupedList.placementTasks');
      case TaskEntity.FACILITIES:
        return t('notification.taskManagement.groupedList.facilityTasks');
      case TaskEntity.ORDERS:
        return t('notification.taskManagement.groupedList.orderTasks');
      default:
        return t('notification.taskManagement.groupedList.entity');
    }
  };

  const returnEntityColumnTitle = () => {
    switch (taskEntity) {
      case TaskEntity.CANDIDATES:
        return t('notification.taskManagement.groupedColumn.candidateTasks');
      case TaskEntity.PLACEMENTS:
        return t('notification.taskManagement.groupedColumn.placementTasks');
      case TaskEntity.FACILITIES:
        return t('notification.taskManagement.groupedColumn.facilityTasks');
      case TaskEntity.ORDERS:
        return t('notification.taskManagement.groupedColumn.orderTasks');
      default:
        return t('notification.taskManagement.groupedColumn.entity');
    }
  };

  const defaultColumns: GridColumns = [
    {
      ...XGridDefaultColumn,
      field: 'entityId',
      headerName: returnEntityColumnTitle(),
      width: 120,
      renderCell: task =>
        !(taskEntity === TaskEntity.ORDERS) ? (
          <div style={{ lineHeight: '20px', fontWeight: 500 }}>
            <div style={{ fontSize: '14px' }}>{task.row?.name}</div>
            <div>{task.row?.entityId ? task.row?.entityId : '-'}</div>
          </div>
        ) : (
          <div style={{ lineHeight: '20px', fontWeight: 500 }}>{task.row?.entityId}</div>
        ),
    },
    {
      ...XGridDefaultColumn,
      field: 'taskCount',
      width: 120,
      headerName: t('notification.taskManagement.title'),
    },
  ];

  const placementColumns: GridColumns = [
    {
      ...defaultTaskWordDeskColumn,
      field: 'entityId',
      headerName: returnEntityColumnTitle(),
      width: 90,
      renderCell: task => <div style={{ lineHeight: '20px', fontWeight: 500 }}>{task.row?.entityId}</div>,
    },
    {
      ...defaultTaskWordDeskColumn,
      field: 'candidateName',
      headerName: t('notification.taskManagement.groupedColumn.candidateTasks'),
      width: 150,
      renderCell: task => (
        <div className={classes.columnValues} style={{ lineHeight: '20px', fontWeight: 500 }}>
          <div style={{ fontSize: '14px', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {task.row?.candidateName}
          </div>
          <div style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {task.row?.candidateId ? task.row?.candidateId : missingField}
          </div>
        </div>
      ),
    },
    {
      ...defaultTaskWordDeskColumn,
      field: 'taskCount',
      headerName: t('notification.taskManagement.title'),
      width: 75,
    },
    {
      ...defaultTaskWordDeskColumn,
      field: 'placementStatus',
      headerName: t('notification.taskManagement.groupedColumn.status'),
      width: 120,
    },
    {
      ...defaultTaskWordDeskColumn,
      field: 'placementStartDate',
      headerName: t('notification.taskManagement.groupedColumn.startDate'),
      width: 120,
    },
  ];

  const fetchColumns = () => {
    if (taskEntity === TaskEntity.PLACEMENTS) {
      setColumns(placementColumns);
    } else {
      setColumns(defaultColumns);
    }
  };

  useEffect(() => {
    setColumns([]);
    fetchColumns();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [taskEntity]);

  const resizeGrid = useCallback(event => {
    updateGridHeight();
  }, []);

  useEffect(() => {
    updateGridHeight();
    window.addEventListener('resize', resizeGrid, true);
    if (rows?.items?.length > 0) {
      if (taskEntity === TaskEntity.PLACEMENTS) {
        setGridDetails(formatPlacementGridDetails(rows?.items));
      } else {
        setGridDetails(rows?.items);
      }
    } else {
      setGridDetails([]);
    }
    return () => {
      window.removeEventListener('resize', resizeGrid);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, resizeGrid]);

  const updateGridHeight = () => {
    const a = document.getElementById('details-page-paper')?.offsetHeight;
    const b = getDetailsPageSupportComponentHeight();
    if (a && b) {
      // -100 to account for title
      setGridHeight(a - b - (document.getElementById('xgrid-title-container')?.offsetHeight ?? 0) - 12);
    }
  };

  const onRowSelected = (params: GridRowParams) => {
    const isSelected = gridApi?.current?.isRowSelected(params.id);
    const filterSet = _cloneDeep(Object.fromEntries(filterAttributes?.filters || []));
    const filterChips = _cloneDeep(filterAttributes?.selectedChipFilters);
    const { entityId, name } = params.row;
    const newFilter = {
      id: entityId,
      value: entityId,
      name: name !== ' ' ? name : entityId,
    };
    setDontUpdateData(true);
    if (filterSet && filterChips) {
      switch (taskEntity) {
        case TaskEntity.CANDIDATES:
          if (isSelected) {
            filterChips.candidateIds = [entityId];
            filterSet.candidateIds = [newFilter];
            filterSet.candidateName = [];
            setValue('candidateIds', [newFilter]);
            setValue('candidateName', []);
          } else {
            filterChips.candidateIds = [];
            filterSet.candidateIds = [];
            setValue('candidateIds', []);
          }
          break;
        case TaskEntity.PLACEMENTS:
          if (isSelected) {
            filterChips.placementIds = [entityId];
            filterSet.placementIds = [newFilter];
            setValue('placementIds', [newFilter]);
          } else {
            filterChips.placementIds = [];
            filterSet.placementIds = [];
            setValue('placementIds', []);
          }
          break;
        case TaskEntity.FACILITIES:
          if (isSelected) {
            filterChips.facilityIds = [entityId];
            filterSet.facilityIds = [newFilter];
            setValue('facilityIds', [newFilter]);
            setValue('facility', []);
            filterSet.facility = [];
          } else {
            filterChips.facilityIds = [];
            filterSet.facilityIds = [];
            setValue('facilityIds', []);
          }
          break;
        case TaskEntity.ORDERS:
          if (isSelected) {
            filterChips.orderIds = [entityId];
            filterSet.orderIds = [newFilter];
            setValue('orderIds', [newFilter]);
          } else {
            filterChips.orderIds = [];
            filterSet.orderIds = [];
            setValue('orderIds', []);
          }
          break;
      }
      dispatch(gridStateActions.setSelectedChips(filterChips));
      dispatch(gridStateActions.setSelectedFilters(filterSet));
    }
  };

  const onSortModelChanged = (model: GridSortModel, details: GridCallbackDetails) => {
    if (gridApi?.current?.state && (model?.length > 0 || !isEqual(defaultSortModel, sortModel))) {
      const pagination = gridApi.current.state.pagination;
      const m =
        model?.length > 0
          ? {
              column: model[0]?.field,
              direction: model[0]?.sort,
            }
          : undefined;
      setSortModel(
        model?.length > 0
          ? [
              {
                field: model[0]?.field,
                sort: model[0]?.sort,
              },
            ]
          : defaultSortModel,
      );
      setSortedColumn(m);
      fetchTaskGroupedListData(pagination.pageSize, pagination.page + 1, m);
    }
  };

  const onPageChange = (page: number, details: GridCallbackDetails) => {
    const pagination = gridApi.current.state.pagination;
    if (pagination) {
      fetchTaskGroupedListData(pagination.pageSize, page + 1, sortedColumn);
    }
  };

  const GroupedListXGrid = useMemo(() => {
    return (
      <XGrid
        apiRef={gridApi}
        hideFooterSelectedRowCount
        gridTag={gridTags.ENTITYGRIDTAG}
        onRowClick={onRowSelected}
        autoHeight={false}
        height={gridHeight}
        columns={columns}
        rows={gridDetails || []}
        headerHeight={45}
        rowHeight={80}
        title={returnEntityTitle()}
        defaultRowsPerPage={50}
        pageSize={50}
        paginationMode={'server'}
        page={rows?.paging?.pageNumber && rows?.paging?.pageNumber - 1}
        sortingMode={'server'}
        rowCount={
          rows?.paging && rows?.paging?.total > totalCountErrorThreshold
            ? totalCountErrorThreshold
            : rows?.paging?.total
        }
        rowCountDisplay={rows?.paging?.total}
        onPageChange={onPageChange}
        defaultSortModel={defaultSortModel}
        onSortModelChange={onSortModelChanged}
        loading={loading}
        sortModel={sortModel}
        paginationProps={{
          hideResultsPerPageSelection: true,
          hidePageCount: true,
        }}
        disableStateRowSelection={true}
        toolbarProps={{
          refreshButton: true,
          refreshGrid: () => {
            const pagination = gridApi.current.state.pagination;
            if (pagination) {
              fetchTaskGroupedListData(pagination.pageSize, pagination.page + 1, sortedColumn);
            }
          },
          resetColumns: () => {
            fetchColumns();
            dispatch(
              globalActions.setSnackBar({
                message: 'Columns have been reset.',
                severity: 'success',
              }),
            );
          },
        }}
        components={{
          NoRowsOverlay:
            filterAttributes?.selectedChipFilters && searchSuggestion
              ? () => <SearchRecommendation entity={SearchType.tasks} />
              : undefined,
        }}
      />
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [columns, gridDetails, loading, rows?.paging, onPageChange, onSortModelChanged, sortModel, gridHeight]);

  return <div className={classes.groupContainer}>{GroupedListXGrid}</div>;
};
